/**
 * ViewDataContext — Bridges existing App hook data to view widgets
 *
 * Widgets consume this context for read-only market data.
 * Decouples widgets from App's prop-drilling chain.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { NBAMarketRow, KalshiOrder, Position } from '@/types';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { GameData, SportsLoadingState } from '@/lib/sportsStream/types';
import type { Sport, MarketType } from '@/lib/sportsDiscovery';

export interface ViewDataContextValue {
  /** Current market rows */
  markets: NBAMarketRow[];
  /** Consolidated game books (multi-venue) */
  consolidatedGames: ConsolidatedGameBooks[];
  consolidatedLoading: boolean;
  /** Sports game data by sport key */
  sportsGames: Map<string, GameData[]>;
  sportsLoading: Map<string, boolean>;
  sportsLoadingStates: Map<string, SportsLoadingState>;
  /** Feed health */
  feedStatus: 'live' | 'offline';
  lastUpdateTime: string;
  /** Orders (read-only in views) */
  orders: KalshiOrder[];
  /** Position data */
  positions: Position[];
  totalPnl: number;
  /** Sport market type switching */
  onSportMarketChange: (sport: Sport, marketType: MarketType) => Promise<void>;
}

const ViewDataContext = createContext<ViewDataContextValue | null>(null);

interface ViewDataProviderProps {
  children: ReactNode;
  value: ViewDataContextValue;
}

export function ViewDataProvider({ children, value }: ViewDataProviderProps) {
  return <ViewDataContext.Provider value={value}>{children}</ViewDataContext.Provider>;
}

export function useViewData(): ViewDataContextValue {
  const ctx = useContext(ViewDataContext);
  if (!ctx) {
    throw new Error('useViewData must be used within <ViewDataProvider>');
  }
  return ctx;
}
